import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import QuoteForm from '../../../components/feature/QuoteForm';

export default function ParkingLot() {
  const services = [
    {
      title: 'New Parking Lot Construction',
      description: 'Complete parking lot design and installation with proper drainage and ADA compliance.',
      icon: 'ri-building-line',
    },
    {
      title: 'Parking Lot Striping',
      description: 'Professional line striping, ADA markings, and traffic control signage.',
      icon: 'ri-pencil-ruler-2-line',
    },
    {
      title: 'Lot Resurfacing',
      description: 'Overlay worn parking lots with new asphalt for refreshed appearance and performance.',
      icon: 'ri-refresh-line',
    },
    {
      title: 'Sealcoating',
      description: 'Commercial-grade sealcoating to protect and extend parking lot lifespan.',
      icon: 'ri-shield-check-line',
    },
    {
      title: 'Crack & Pothole Repair',
      description: 'Comprehensive repair services to maintain safe, smooth parking surfaces.',
      icon: 'ri-tools-line',
    },
    {
      title: 'Drainage Solutions',
      description: 'Catch basins, grading, and stormwater management systems.',
      icon: 'ri-water-flash-line',
    },
  ];

  const faqs = [
    {
      question: 'How much does parking lot striping cost in Lincoln NE?',
      answer: 'Parking lot striping costs $0.15-$0.30 per linear foot for standard lines, $50-$100 per handicap symbol, and $25-$50 per arrow or stop bar. A typical 50-space parking lot costs $800-$1,500 for complete striping. Prices include layout, paint, and ADA-compliant markings.',
    },
    {
      question: 'How often should parking lots be restriped?',
      answer: 'Restripe parking lots every 1-2 years depending on traffic volume. High-traffic lots need annual striping. Faded lines create confusion and liability. We recommend restriping after sealcoating for fresh, visible markings that enhance safety and appearance.',
    },
    {
      question: 'What is parking lot resurfacing?',
      answer: 'Resurfacing (overlay) involves applying 1.5-2 inches of new asphalt over existing pavement. It\'s ideal when the surface is worn but the base is sound. Resurfacing costs $2-$4 per square foot, about half the cost of complete replacement, and extends lot life by 10-15 years.',
    },
    {
      question: 'How many ADA parking spaces do I need?',
      answer: 'ADA requirements vary by total spaces: 1-25 spaces need 1 accessible space, 26-50 need 2, 51-75 need 3, etc. One in every 6 accessible spaces must be van-accessible. We ensure your parking lot meets federal and local accessibility requirements to avoid penalties.',
    },
    {
      question: 'Can you work around our business hours?',
      answer: 'Absolutely! We schedule parking lot work during off-hours, evenings, or weekends to minimize disruption. Most striping takes 2-4 hours and can be used immediately after drying (1-2 hours). Larger projects are phased to maintain access. We coordinate with property managers for convenient scheduling.',
    },
    {
      question: 'How long does parking lot sealcoating last?',
      answer: 'Commercial parking lot sealcoating lasts 2-3 years with proper application. High-traffic areas may need annual treatment. Sealcoating protects against UV damage, water infiltration, and chemical spills, extending pavement life by 10-15 years. We offer maintenance contracts for scheduled service.',
    },
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Inter, sans-serif' }}>
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-primary-500 text-sm mb-4 tracking-wider">Services / Commercial</p>
            <h1 className="text-5xl md:text-6xl font-black mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Commercial Parking Lot Services
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Professional parking lot striping, resurfacing, and maintenance in Lincoln, Nebraska
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <div>
              <img
                src="https://static.readdy.ai/image/cd984ab7ba08a0f8b6f75adc7751930e/fbbbcf9620ccb8528b4b2301fbbd45c8.jpeg"
                alt="Lincoln NE parking lot striping"
                className="w-full h-96 object-cover rounded-2xl shadow-xl"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = 'https://via.placeholder.com/800x600?text=Parking+Lot+Striping';
                }}
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Professional Parking Lot Services
              </h2>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                Your parking lot is a critical business asset that impacts customer experience, safety, and property value. Faded striping, cracked pavement, and poor maintenance send the wrong message. We provide comprehensive parking lot services that enhance appearance, ensure compliance, and extend pavement life.
              </p>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                Specializing in commercial properties throughout Lincoln and Lancaster County, we handle everything from new construction to routine maintenance. Our services include striping, sealcoating, resurfacing, repairs, and ADA compliance upgrades.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                We work efficiently to minimize business disruption, scheduling projects during off-hours or phasing work to maintain access. With professional equipment and experienced crews, we deliver quality results on time and within budget.
              </p>
              <a href="tel:3084705637" className="inline-block bg-primary-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-primary-700 transition-colors cursor-pointer">
                REQUEST PARKING LOT QUOTE
              </a>
            </div>
          </div>

          {/* Services Grid */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold mb-12 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Parking Lot Services
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <div key={index} className="bg-gray-50 p-8 rounded-2xl border-2 border-gray-200 hover:border-primary-600 transition-all">
                  <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mb-6">
                    <i className={`${service.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {service.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {service.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Detailed Content */}
          <div className="prose prose-lg max-w-none mb-16">
            <h2 className="text-4xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Parking Lot Striping in Lincoln NE
            </h2>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Clear, visible parking lot striping is essential for safety, traffic flow, and ADA compliance. Faded lines create confusion, reduce parking capacity, and increase liability. Professional striping enhances your property's appearance and ensures customers can navigate safely.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              We provide complete parking lot striping services using high-quality traffic paint or thermoplastic for durability. Our services include layout design, parking stalls, handicap spaces and symbols, arrows, stop bars, crosswalks, fire lanes, loading zones, and custom markings.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Professional Line Striping
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Our striping process ensures accurate, durable markings. We start with layout design that maximizes parking capacity while meeting code requirements. Standard parking stalls are 9 feet wide by 18 feet long. Compact spaces are 8 feet wide. Drive aisles are 24 feet wide for two-way traffic, 12-14 feet for one-way.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              We use professional striping equipment that applies paint at consistent thickness and width. Lines are typically 4 inches wide, though 6-inch lines are used for emphasis. We apply paint at proper thickness (15-20 mils) for visibility and longevity.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Traffic paint dries in 1-2 hours and lasts 1-2 years. Thermoplastic is heat-applied, lasts 3-5 years, and provides superior durability for high-traffic areas. We help you choose the right material for your traffic volume and budget.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              ADA Compliance and Accessible Parking
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Federal law requires accessible parking spaces based on total parking capacity. Requirements include: proper number of spaces (1 per 25 for small lots, increasing with size), van-accessible spaces (1 in 6 accessible spaces), 8-foot wide spaces with 5-foot access aisles (8-foot aisles for van spaces), proper signage (vertical signs at each space), and accessible routes to building entrances.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              We ensure full ADA compliance with proper space dimensions, slopes not exceeding 2%, compliant signage, and accessible routes. Non-compliance can result in lawsuits and penalties up to $75,000 for first violations. We help property owners meet requirements and avoid costly penalties.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Parking Lot Resurfacing
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              When parking lot surfaces show significant wear but the base is sound, resurfacing is the ideal solution. We apply 1.5-2 inches of new asphalt over existing pavement, creating a smooth, durable surface at 40-60% the cost of complete replacement.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Resurfacing addresses surface deterioration, minor cracking, fading, and wear patterns. We mill edges for smooth transitions, repair any base issues, apply tack coat for adhesion, pave with hot asphalt, and compact to proper density. The result looks and performs like new pavement.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Resurfacing costs $2-$4 per square foot installed. A 10,000 sq ft parking lot costs $20,000-$40,000 to resurface vs $40,000-$70,000 for complete replacement. Resurfacing extends lot life by 10-15 years and includes fresh striping.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Commercial Sealcoating
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Commercial parking lots require professional-grade sealcoating every 2-3 years. We use refined coal tar or asphalt emulsion sealers with sand additives for traction. Our process includes power washing, crack filling, oil spot treatment, two coats of sealer, and fresh striping.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Sealcoating protects against UV damage, water infiltration, chemical spills, and oxidation. It extends pavement life by 10-15 years and maintains professional appearance. We schedule work during off-hours to minimize business disruption.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Commercial sealcoating costs $0.12-$0.20 per square foot. A 10,000 sq ft parking lot costs $1,200-$2,000 for complete sealcoating including crack filling and striping. Volume discounts are available for large properties and multi-year contracts.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Crack and Pothole Repair
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Regular crack and pothole repair maintains safe parking surfaces and prevents major problems. We provide comprehensive repair services including crack filling, pothole patching, edge repair, and infrared repairs for seamless results.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Cracks are filled with hot rubberized filler that remains flexible through temperature extremes. Potholes are repaired with hot asphalt using proper preparation and compaction. We address repairs promptly to prevent water infiltration and pavement failure.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Drainage Solutions
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Proper drainage is critical for parking lot longevity. Standing water accelerates pavement deterioration and creates ice hazards in winter. We provide comprehensive drainage solutions including catch basins, grading, drainage swales, and stormwater management systems.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              We assess drainage patterns, identify problem areas, and design solutions that comply with Lincoln stormwater regulations. Proper drainage prevents water infiltration that undermines the base and causes pavement failure.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Maintenance Programs
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              We offer customized maintenance programs for commercial properties. Programs include scheduled inspections, crack filling, sealcoating, striping, and repairs. Regular maintenance prevents major problems, extends pavement life, and budgets costs predictably.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Maintenance contracts provide priority scheduling, discounted rates, and peace of mind. We work with property managers, facility directors, and business owners to create programs that fit their needs and budgets. Multi-year contracts offer additional savings.
            </p>
          </div>

          {/* Project Gallery */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold mb-12 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Parking Lot Projects
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <img
                src="https://public.readdy.ai/ai/img_res/b39b282f3a3266f5374898ac1c62f9a7.jpg"
                alt="Striped parking lot"
                className="w-full h-64 object-cover rounded-2xl shadow-xl"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://via.placeholder.com/600x400?text=Striped+Parking+Lot';
                }}
              />
              <img
                src="https://static.readdy.ai/image/cd984ab7ba08a0f8b6f75adc7751930e/eefd9db5ab6b6d4ca0f047a300097eb2.jpeg"
                alt="ADA parking spaces"
                className="w-full h-64 object-cover rounded-2xl shadow-xl"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://via.placeholder.com/600x400?text=ADA+Parking+Spaces';
                }}
              />
              <img
                src="https://static.readdy.ai/image/cd984ab7ba08a0f8b6f75adc7751930e/d61fb8a672bcb261a0966b058d08bc94.jpeg"
                alt="Resurfaced parking lot"
                className="w-full h-64 object-cover rounded-2xl shadow-xl"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://via.placeholder.com/600x400?text=Resurfaced+Parking+Lot';
                }}
              />
            </div>
          </div>

          {/* FAQs */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold mb-12 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-gray-50 p-8 rounded-2xl border-2 border-gray-200">
                  <h3 className="text-2xl font-bold mb-4 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {faq.question}
                  </h3>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Quote Form */}
          <div className="bg-gray-50 p-12 rounded-2xl">
            <h2 className="text-4xl font-bold mb-6 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Request Parking Lot Services
            </h2>
            <p className="text-xl text-gray-700 text-center mb-8">
              Professional striping, maintenance, and repair for your commercial property
            </p>
            <QuoteForm />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
